import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import builderLogo from '../../assets/Builder-Logo.png';
import { purchaseOrderService, PurchaseOrder } from '../../services/purchaseOrderService';
import supplierService from '../../services/supplierService';
import { format, parseISO } from 'date-fns';
import { API_URL, AUTH_TOKEN_KEY } from '../../config';
// @ts-ignore
import html2pdf from 'html2pdf.js';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 95vw;
  max-width: 1200px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  padding: 8px 16px;
  border-bottom: none;
  text-align: right;
`;

const ModalTitle = styled.h2`
  font-size: 16px;
  color: #333;
  margin: 0;
  display: none;
`;

const ModalBody = styled.div`
  padding: 16px;
`;

const PrintableContent = styled.div`
  background: white;
  font-family: Arial, sans-serif;
  padding: 20px;
  max-width: 1100px;
  margin: 0 auto;
  
  @media print {
    margin: 0;
    padding: 20px;
    font-size: 12px;
    page-break-inside: avoid;
    transform: rotate(0deg);
  }
`;

const POContainer = styled.div`
  border: 2px solid #333;
  padding: 20px;
  margin-bottom: 20px;
  background: white;
`;

const POTitle = styled.h1`
  text-align: center;
  font-size: 28px;
  font-weight: bold;
  color: #333;
  margin: 0 0 20px 0;
  padding: 0;
`;

const POHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
`;

const CompanyLogo = styled.div`
  width: 180px;
  display: flex;
  flex-direction: column;
  
  img {
    max-width: 100%;
    height: auto;
    margin-bottom: 5px;
  }
  
  .tagline {
    font-style: italic;
    font-size: 14px;
    color: #042B41;
    margin-top: 0;
    font-family: cursive;
  }
`;

const CompanyInfo = styled.div`
  text-align: right;
  font-size: 11px;
  line-height: 1.3;
  color: #333;
  
  p {
    margin: 1px 0;
  }
  
  .company-name {
    font-weight: bold;
    font-size: 12px;
  }
`;

const MainContentContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
`;

const LeftSection = styled.div`
  flex: 1;
`;

const RightSection = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
`;

const InfoTable = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  margin-bottom: 15px;
  border: 2px solid #333;
`;

const InfoCell = styled.div`
  padding: 12px;
  border: 1px solid #333;
  font-size: 13px;
  min-height: 50px;
`;

const InfoLabel = styled.div`
  font-weight: bold;
  font-size: 12px;
  margin-bottom: 6px;
  color: #333;
`;

const POLabel = styled.div`
  background-color: #0096db;
  color: white;
  padding: 8px 15px;
  text-align: center;
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 0;
  border: 2px solid #333;
`;

const PODetails = styled.div`
  display: flex;
  flex-direction: column;
  border: 2px solid #333;
  border-top: none;
  background-color: white;
`;

const PODetail = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 6px 15px;
  font-size: 13px;
  border-bottom: 1px solid #333;
  
  &:last-child {
    border-bottom: none;
  }
  
  span:first-child {
    font-weight: 600;
  }
  
  span:last-child {
    text-align: right;
    font-weight: 500;
  }
`;

const POTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 15px 0;
  font-size: 12px;
  border: 2px solid #333;
  
  th, td {
    border: 1px solid #333;
    padding: 6px 4px;
    text-align: center;
  }
  
  th {
    background-color: #042B41;
    color: white;
    font-size: 11px;
    font-weight: 600;
    text-align: center;
    vertical-align: middle;
    height: 35px;
  }
  
  td {
    padding: 6px 4px;
    vertical-align: middle;
    font-size: 11px;
    height: 30px;
  }
  
  .code-column { 
    width: 15%; 
    text-align: left;
  }
  .description-column { 
    width: 30%; 
    text-align: left;
  }
  .quantity-column { width: 12%; }
  .delivery-date-column { 
    width: 15%; 
    text-align: center;
  }
  .total-price-column { 
    width: 13%; 
    text-align: right;
  }
`;

const OrderTotal = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin-left: auto;
  width: 220px;
  background-color: #f8f9fa;
  border: 2px solid #333;
  margin-top: 15px;
  
  div {
    padding: 6px 12px;
    border: 1px solid #333;
    font-size: 13px;
  }
  
  div:nth-child(even) {
    text-align: right;
    font-weight: 700;
    background-color: white;
  }
  
  div:nth-child(odd) {
    text-align: left;
    font-weight: 600;
    background-color: #e9ecef;
  }
`;

const POInfo = styled.div`
  margin: 20px 0 0 0;
  padding: 12px;
  border: 2px solid #333;
  font-size: 11px;
  line-height: 1.4;
  background-color: #f9f9f9;
  
  p {
    margin: 4px 0;
  }
  
  p:first-child {
    margin-top: 0;
  }
  
  p:last-child {
    margin-bottom: 0;
  }
  
  strong {
    font-weight: 700;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
`;

const Button = styled.button<{ primary?: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  background-color: ${props => props.disabled ? '#ccc' : (props.primary ? '#042B41' : 'white')};
  color: ${props => props.disabled ? '#666' : (props.primary ? 'white' : '#042B41')};
  border: ${props => props.primary ? 'none' : '1px solid #042B41'};
  opacity: ${props => props.disabled ? 0.6 : 1};
  
  &:hover {
    background-color: ${props => props.disabled ? '#ccc' : (props.primary ? '#0A3D5A' : '#f9fafb')};
  }
`;

interface POItem {
  id?: string;
  purchase_order_id?: number;
  sku: string;
  style_code?: string | null;
  description: string;
  quantity_ordered: number;
  quantity_received?: number;
  expected_delivery_date?: string;
  total: number;
  unit_price?: number;
  total_price?: number;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

interface POData {
  poNumber: string;
  date: string;
  supplierName: string;
  items: POItem[];
  orderTotal: number;
  gst: number;
  grandTotal: number;
  notes: string;
}

interface POViewProps {
  poId: string;
  onClose: () => void;
  printMode?: boolean;
}

// Add PO PDF generation function
const generatePOPdf = async (poId: string): Promise<Blob> => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);

  if (!token) {
    throw new Error("Authentication required to generate PO PDF");
  }

  console.log("Attempting to generate PDF for PO:", poId);
  console.log(
    "Using endpoint:",
    `${API_URL}/api/v1/purchase-orders/${poId}/pdf`
  );

  try {
    const response = await fetch(
      `${API_URL}/api/v1/purchase-orders/${poId}/pdf`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/pdf",
        },
      }
    );

    console.log("PDF generation response status:", response.status);
    console.log("PDF generation response headers:", response.headers);

    if (!response.ok) {
      // Log detailed error information
      const errorText = await response.text();
      console.error("PDF generation failed with status:", response.status);
      console.error("Error response body:", errorText);

      if (response.status === 404) {
        throw new Error(
          "PDF generation endpoint not found. Please contact support."
        );
      } else if (response.status === 403) {
        throw new Error("You do not have permission to download this PO.");
      } else if (response.status === 401) {
        throw new Error("Authentication required to generate PO PDF");
      } else {
        throw new Error(
          `Failed to generate PO PDF: ${response.status} ${response.statusText}`
        );
      }
    }

    const blob = await response.blob();
    console.log("PDF blob generated successfully, size:", blob.size, "bytes");
    return blob;
  } catch (error) {
    console.error("Error in generatePOPdf:", error);
    throw error;
  }
};

const POView: React.FC<POViewProps> = ({ poId, onClose, printMode }) => {
  const [po, setPo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [supplierName, setSupplierName] = useState<string>('');
  const [downloading, setDownloading] = useState(false);
  const pdfRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Fetch PO data when component mounts or poId changes
  useEffect(() => {
    const fetchPOData = async () => {
      // Reset all state when poId changes
      setLoading(true);
      setError(null);
      setPo(null);
      setSupplierName('');
      setDownloading(false);

      try {
        console.log('Fetching PO with ID:', poId);
        
        // Validate and convert the PO ID
        let numericPoId: number;
        
        // Handle both numeric IDs and PO number strings like "PO-12345"
        if (typeof poId === 'string' && poId.startsWith('PO-')) {
          const numericPart = poId.replace('PO-', '');
          numericPoId = parseInt(numericPart);
          if (isNaN(numericPoId) || numericPoId <= 0) {
            throw new Error(`Invalid PO format: ${poId}. Could not extract valid numeric ID.`);
          }
          console.log('Extracted numeric ID from PO string:', numericPoId);
        } else {
          numericPoId = parseInt(poId);
          if (isNaN(numericPoId) || numericPoId <= 0) {
            throw new Error(`Invalid PO ID: ${poId}. Expected a positive number.`);
          }
        }
        
        const poData: any = await purchaseOrderService.getPurchaseOrderById(numericPoId);
        console.log('PO data received:', poData);

        // Fetch BWA pricing and override total for each detail if found
        let itemsArray: any[] = [];
        let isDetailsKey = true;
        if (poData.details && Array.isArray(poData.details)) {
          itemsArray = poData.details;
          isDetailsKey = true;
        } else if (poData.items && Array.isArray(poData.items)) {
          itemsArray = poData.items;
          isDetailsKey = false;
        }

        const detailsWithBwaPricing = await Promise.all(
          itemsArray.map(async (detail) => {
            if (detail.sku) {
              try {
                const pricingResult = await supplierService.getSkuPricing(detail.sku);
                if (pricingResult && pricingResult.success && pricingResult.bwa_buy_price_50_ex_gst !== undefined && pricingResult.bwa_buy_price_50_ex_gst !== null) {
                  const bwaPrice = parseFloat(pricingResult.bwa_buy_price_50_ex_gst.toString()) || 0;
                  const quantity = detail.quantity_ordered !== undefined ? detail.quantity_ordered : detail.quantity;
                  const quantityNum = typeof quantity === 'string' ? parseFloat(quantity) || 0 : quantity || 0;
                  const calculatedTotal = Math.round(quantityNum * bwaPrice * 100) / 100;
                  return {
                    ...detail,
                    total: calculatedTotal,
                    total_price: calculatedTotal,
                  };
                }
              } catch (error) {
                console.warn(`Error fetching BWA pricing for SKU ${detail.sku} in POView:`, error);
              }
            }
            return detail;
          })
        );

        // Recalculate total_amount based on the ex-GST item totals
        const recalculatedTotalAmount = detailsWithBwaPricing.reduce(
          (sum, item) => sum + (item.total || item.total_price || 0),
          0
        );

        const poWithCorrectPricing = {
          ...poData,
          [isDetailsKey ? 'details' : 'items']: detailsWithBwaPricing,
          total_amount: recalculatedTotalAmount
        };

        setPo(poWithCorrectPricing);
        setSupplierName(poData.supplier_name || 'N/A');
      } catch (err) {
        console.error('Error fetching PO:', err);
        let errorMessage = 'Failed to load purchase order. Please try again.';

        // Provide more specific error messages based on the error type
        if (err && typeof err === 'object' && 'message' in err) {
          errorMessage = (err as Error).message;
        } else if (err && typeof err === 'object' && 'response' in err) {
          const apiError = err as any;
          if (apiError.response?.status === 404) {
            errorMessage = 'Purchase order not found. It may have been deleted or the ID is incorrect.';
          } else if (apiError.response?.status === 403) {
            errorMessage = 'You do not have permission to view this purchase order.';
          } else if (apiError.response?.status === 422) {
            errorMessage = 'Invalid purchase order ID. Please check the ID and try again.';
          } else if (apiError.response?.status >= 500) {
            errorMessage = 'Server error. Please try again later.';
          }
        }

        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (poId) {
      fetchPOData();
    } else {
      setError('No PO ID provided');
      setLoading(false);
    }
  }, [poId]); // Re-run when poId changes

  const generateClientSidePdf = async () => {
    if (!pdfRef.current || downloading) return;

    setDownloading(true);

    try {
      console.log('Generating PDF using html2pdf.js');

      // Get PO number for filename
      const poNumber = po?.po_number || po?.reference_number || 'purchase-order';

      const options = {
        margin: 10,
        filename: `${poNumber}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait'
        }
      };

      await html2pdf().set(options).from(pdfRef.current).save();
      console.log('PDF generated and downloaded successfully');

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownload = async () => {
    if (!poId || downloading) return;

    setDownloading(true);

    try {
      if (printMode) {
        // In print mode, just open the print dialog directly
        console.log('Print mode activated, opening print dialog');
        window.print();
        return;
      }

      console.log('Trying backend PDF generation first');

      try {
        // Try to generate the PDF blob from the backend first
        const pdfBlob = await generatePOPdf(poId);
        console.log('Backend PDF generated successfully, size:', pdfBlob.size, 'bytes');

        // Create a download URL
        const downloadUrl = window.URL.createObjectURL(pdfBlob);

        // Get PO number for filename
        const poNumber = po?.po_number || po?.reference_number || 'purchase-order';

        // Create a temporary download link
        const downloadLink = document.createElement('a');
        downloadLink.href = downloadUrl;
        downloadLink.download = `${poNumber}.pdf`;
        downloadLink.style.display = 'none';

        // Append to body, click, and remove
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        // Clean up the URL
        window.URL.revokeObjectURL(downloadUrl);

        console.log('Backend PDF download completed successfully');
        return;

      } catch (backendError) {
        console.warn('Backend PDF generation failed, falling back to client-side generation:', backendError);

        // Fallback to client-side PDF generation
        await generateClientSidePdf();
      }

    } catch (error) {
      console.error('Error in download process:', error);
      alert('Failed to download PO PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (dateStr: string): string => {
    try {
      if (!dateStr) return 'N/A';
      const date = parseISO(dateStr);
      return format(date, 'dd/MM/yyyy');
    } catch (err) {
      return dateStr || 'N/A';
    }
  };

  if (loading) {
    return (
      <ModalOverlay>
        <ModalContainer>
          <ModalHeader>
            <ModalTitle>Loading Purchase Order</ModalTitle>
          </ModalHeader>
          <ModalBody style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Loading purchase order data</p>
          </ModalBody>
        </ModalContainer>
      </ModalOverlay>
    );
  }

  if (error || !po) {
    return (
      <ModalOverlay>
        <ModalContainer>
          <ModalHeader>
            <ModalTitle>Error</ModalTitle>
          </ModalHeader>
          <ModalBody style={{ textAlign: 'center', padding: '2rem' }}>
            <p>{error || 'Failed to load purchase order data'}</p>
            <Button onClick={onClose}>Close</Button>
          </ModalBody>
        </ModalContainer>
      </ModalOverlay>
    );
  }

  // Extract data from API response with better error handling
  const poNumber = po.po_number || po.reference_number || 'N/A';
  const date = formatDate(po.date || po.order_date);

  // Handle items array - it could be nested in a 'details' property or be the direct array
  let itemsArray: any[] = [];
  if (po.details) {
    if (Array.isArray(po.details)) {
      itemsArray = po.details;
    }
  } else if (po.items && Array.isArray(po.items)) {
    itemsArray = po.items;
  }

  console.log('Processing items array:', itemsArray);

  const items: POItem[] = itemsArray.map((item: any, index: number) => {
    console.log(`Processing item ${index}:`, item);

    return {
      ...item,
      sku: item.sku || item.sku_code || item.code || 'N/A',
      style_code: item.style_code || null,
      description: item.description || 'N/A',
      quantity_ordered: item.quantity_ordered || item.quantity || 0,
      expected_delivery_date: item.expected_delivery_date,
      total: item.total || item.total_price || 0
    };
  });

  // Calculate financial totals with better error handling
  const orderTotal = typeof po.total_amount === 'string'
    ? parseFloat(po.total_amount) || 0
    : (po.total_amount || 0);

  const gst = orderTotal * 0.1; // Calculate 10% GST
  const grandTotal = orderTotal + gst;

  console.log('Rendered PO data:', {
    poNumber,
    supplierName,
    date,
    itemsCount: items.length,
    orderTotal,
    gst,
    grandTotal
  });

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Purchase Order View</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <PrintableContent ref={pdfRef}>
            <POContainer>
              <POTitle>PURCHASE ORDER</POTitle>

              <POHeader>
                <CompanyLogo>
                  <img src={builderLogo} alt="Builders Warehouse" />
                </CompanyLogo>

                <CompanyInfo>
                  <p className="company-name">BUILDERS WAREHOUSE PTY LTD (ACN 654 926 691)</p>
                  <p>Trading as Builders Warehouse Australia</p>
                  <p>(ABN 30 645 926 691)</p>
                  <p></p>
                  <p>Office & Showroom: {po?.store_type?.toLowerCase() === 'sale' ? '257-259 York St, Sale VIC 3850, Australia' : '214 High St, Cranbourne VIC 3977, Australia'}</p>
                  <p>www.builderswarehouseaustralia.com.au</p>
                  <p>Tel: {po?.store_type?.toLowerCase() === 'sale' ? '+61 341189555 | sale@builderswarehouseaustralia.com.au' : '+61 387649142 | cranbourne@builderswarehouseaustralia.com.au'}</p>
                </CompanyInfo>
              </POHeader>

              <MainContentContainer>
                <LeftSection>
                  <InfoTable>
                    <InfoCell>
                      <InfoLabel>SUPPLIER:</InfoLabel>
                      {supplierName}
                    </InfoCell>
                  </InfoTable>
                </LeftSection>

                <RightSection>
                  <POLabel>PURCHASE ORDER</POLabel>
                  <PODetails>
                    <PODetail>
                      <span>PO Number:</span>
                      <span>{poNumber}</span>
                    </PODetail>
                    <PODetail>
                      <span>Issue Date:</span>
                      <span>{date}</span>
                    </PODetail>
                  </PODetails>
                </RightSection>
              </MainContentContainer>

              <POTable>
                <thead>
                  <tr>
                    <th className="code-column">Style Code</th>
                    <th className="description-column">Description</th>
                    <th className="quantity-column">Quantity Ordered</th>
                    <th className="delivery-date-column">Expected Delivery Date</th>
                    <th className="total-price-column">Total (ex GST)</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length > 0 ? (
                    items.map((item, index) => (
                      <tr key={index}>
                        <td className="code-column">{item.style_code || 'N/A'}</td>
                        <td className="description-column">{item.description}</td>
                        <td className="quantity-column">{item.quantity_ordered}</td>
                        <td className="delivery-date-column">
                          {item.expected_delivery_date ? formatDate(item.expected_delivery_date) : 'N/A'}
                        </td>
                        <td className="total-price-column">${item.total?.toFixed(2) || '0.00'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', padding: '20px' }}>
                        No items found
                      </td>
                    </tr>
                  )}
                </tbody>
              </POTable>

              <OrderTotal>
                <div>Order Total:</div>
                <div>${orderTotal.toFixed(2)}</div>
                <div>GST (10%):</div>
                <div>${gst.toFixed(2)}</div>
                <div>Grand Total:</div>
                <div>${grandTotal.toFixed(2)}</div>
              </OrderTotal>

              <POInfo>
                <div style={{ display: 'flex', gap: '20px', justifyContent: 'space-between' }}>
                  <div style={{ flex: 1 }}>
                    <p><strong>Terms & Conditions:</strong></p>
                    <p>• Payment terms: {po.payment_terms || '30 days net'}.</p>
                    <p>• Prices include GST unless otherwise stated.</p>
                    <p>• Delivery charges may apply based on location.</p>
                    <p>• All materials are subject to availability.</p>
                    <p>• Please confirm delivery schedule upon order acceptance.</p>
                    <p>• Special handling requirements may incur additional charges.</p>
                    <p><strong>For any queries, contact us at:</strong> {po?.store_type?.toLowerCase() === 'sale' ? 'sale@builderswarehouse.com.au' : 'cranbourne@builderswarehouse.com.au'}</p>
                    {po.notes && (
                      <>
                        <p><strong>Additional Notes:</strong></p>
                        <p>{po.notes}</p>
                      </>
                    )}
                  </div>
                  <div style={{ flex: 1, borderLeft: '2px solid #333', paddingLeft: '15px' }}>
                    <p><strong>Payment Details:</strong></p>
                    {(() => {
                      const storeType = typeof po?.store_type === 'string' 
                        ? po.store_type.toLowerCase() 
                        : po?.store_type?.name?.toLowerCase();
                      
                      if (storeType === 'sale') {
                        return (
                          <>
                            <p>BSB: 013542</p>
                            <p>ACC: 434148002</p>
                            <p>Account Name: Builders Warehouse Australia</p>
                          </>
                        );
                      } else {
                        // Default to Cranbourne
                        return (
                          <>
                            <p>BSB: 013542</p>
                            <p>Account: 430892002</p>
                            <p>Account Name: Builders Warehouse Australia</p>
                          </>
                        );
                      }
                    })()}
                  </div>
                </div>
              </POInfo>
            </POContainer>
          </PrintableContent>

          <ButtonContainer>
            <Button
              primary
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? 'Generating PDF...' : 'Download PDF'}
            </Button>
            <Button onClick={onClose}>
              Close
            </Button>
          </ButtonContainer>
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default POView;